import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faHeart as faHeartSolid, faAngleLeft, faAngleRight, faFlag, faPhone } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import ContactSafetyModal from './ContactSafetyModal';
import './Ad.css';

// الاستيرادات الجديدة بناءً على تعليمات المدير
import { getLocationDisplay } from '../constants/locations';
import { getAccommodationTypeWithTenants } from '../constants/accommodationTypes';
import { getAmenitiesAr, getAmenitiesEn, getAmenitiesValues } from '../constants/amenities';
import { getTimeAgo } from '../constants/timeUtils'; 

import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, arrayRemove, arrayUnion, setDoc } from 'firebase/firestore';
import { getOptimizedImageUrl } from '../cloudinaryService';

// حد أدنى جديد للتصغير (25%)
const MIN_ZOOM = 0.25;

function Ad({ currentLanguage, getLocalizedText, onOpenLogin }) {
  const { id } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);

  // حالات مودال التحذير قبل التواصل (اتصال / واتساب)
  const [showContactSafetyModal, setShowContactSafetyModal] = useState(false);
  const [pendingContactAction, setPendingContactAction] = useState(null); // 'call' | 'whatsapp'

  // التحقق من المفضلة
  useEffect(() => {
    const checkIfFavorited = async () => {
      if (!listing) return; 
      
      if (!auth.currentUser) {
        setIsFavorited(false);
        setLoadingFavorite(false);
        return;
      }

      try {
        const userRef = doc(db, 'verified_users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const favourites = userSnap.data().favourite_ads_id || [];
          setIsFavorited(favourites.includes(listing.id));
        }
      } catch (error) {
        console.error('Error checking favourite:', error);
      } finally {
        setLoadingFavorite(false);
      }
    };

    checkIfFavorited();
  }, [listing]);

  // تبديل حالة المفضلة
  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!auth.currentUser) {
      alert(getLocalizedText('يجب تسجيل الدخول لإضافة إلى المفضلة', 'Please login to add to favourites'));
      return;
    }

    if (!listing) return;

    setLoadingFavorite(true);

    try {
      const userRef = doc(db, 'verified_users', auth.currentUser.uid);

      if (isFavorited) {
        await updateDoc(userRef, {
          favourite_ads_id: arrayRemove(listing?.id)
        });
        setIsFavorited(false);
      } else {
        await setDoc(userRef, {
          favourite_ads_id: arrayUnion(listing?.id)
        }, { merge: true });

        setIsFavorited(true);
      }
    } catch (error) {
      console.error('Error toggling favourite:', error);
      alert(getLocalizedText('حدث خطأ، حاول مرة أخرى', 'Error, please try again'));
    } finally {
      setLoadingFavorite(false);
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('dir', currentLanguage === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // جلب بيانات الإعلان
  // جلب بيانات الإعلان (التعديل هنا)
useEffect(() => {
  const fetchAd = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'ads', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // جديد: جلب الاسم الكامل من verified_users بناءً على userId
        let postedBy = currentLanguage === 'ar' ? 'مجهول' : 'Anonymous'; // Default لو مش موجود
        if (data.userId) {
          const userRef = doc(db, 'verified_users', data.userId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            postedBy = userSnap.data().fullName || postedBy; // استخدم fullName إذا موجود، وإلا الديفولت
          }
        }
        
        const adData = {
          id: docSnap.id,
          images: data.ad_images || [],
          fullAddress: data.ad_location || '',
          location: {
            ar: getLocationDisplay(data.city, data.region, 'ar'),
            en: getLocationDisplay(data.city, data.region, 'en'),
            city: data.city,
            region: data.region
          },
          price: data.price,
          type: {
            ar: getAccommodationTypeWithTenants(data.accommodation_type, data.tenants_policy, 'ar'),
            en: getAccommodationTypeWithTenants(data.accommodation_type, data.tenants_policy, 'en'),
            value: data.accommodation_type
          },
          phoneNumber: data.ad_contact_phone,
          whatsappNumber: data.ad_contact_whatsapp,
          amenities: {
            ar: getAmenitiesAr(data.amenities),
            en: getAmenitiesEn(data.amenities),
            values: getAmenitiesValues(data.amenities)
          },
          description: data.ad_description || '',
          timeSincePosted: {
            ar: getTimeAgo(data.createdAt, 'ar'),
            en: getTimeAgo(data.createdAt, 'en')
          },
          tenants: data.tenants_policy,
          visitorsPolicy: data.visitors_policy,
          petsPolicy: data.pets_policy,
          priceDuration: data.price_type,
          smokingPolicy: data.smoking_policy,
          insurance: data.insurance,
          priceIncludesBills: data.price_includes_bills,
          // جديد: حقول ممكن ما تكونش موجودة في الإعلانات القديمة، فبنحط قيمة افتراضية آمنة
          hostType: data.host_type || '',
          hostManagerValue: data.host_manager_value || '',
          cleaningExplanation: data.cleaning_explanation || '',
          postedBy: postedBy  // جديد: إضافة الاسم إلى adData
        };
        
        setListing(adData);
      } else {
        setError('Ad not found');
      }
    } catch (err) {
      console.error('Error fetching ad:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  fetchAd();
}, [id, currentLanguage]);

  // دوال التحكم بالصور والاتصال
  const handlePrevImage = () => {
    if (listing && listing.images.length > 0) {
      setCurrentImageIndex((prev) => (prev === 0 ? listing.images.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (listing && listing.images.length > 0) {
      setCurrentImageIndex((prev) => (prev === listing.images.length - 1 ? 0 : prev + 1));
    }
  };

  // --- تحديثات الزوم المطلوبة ---
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 3)); // أقصى حد 300%
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, MIN_ZOOM)); // استخدام الحد الأدنى الجديد
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleCallClick = () => {
  if (!auth.currentUser) {
    onOpenLogin?.();
    return;
  }
  setPendingContactAction('call');
  setShowContactSafetyModal(true);
};

const handleWhatsAppClick = () => {
  if (!auth.currentUser) {
    onOpenLogin?.();
    return;
  }
  setPendingContactAction('whatsapp');
  setShowContactSafetyModal(true);
};

// تنفيذ عملية التواصل الفعلية بعد موافقة المستخدم على إرشادات السلامة
const performCall = () => {
  if (listing) {
    window.location.href = `tel:+2${listing.phoneNumber}`;
  }
};

const performWhatsApp = () => {
  if (listing) {
    window.open(`https://wa.me/2${listing.whatsappNumber}`, '_blank');
  }
};

const handleCloseContactSafetyModal = () => {
  setShowContactSafetyModal(false);
  setPendingContactAction(null);
};

const handleConfirmContact = () => {
  setShowContactSafetyModal(false);
  if (pendingContactAction === 'whatsapp') {
    performWhatsApp();
  } else if (pendingContactAction === 'call') {
    performCall();
  }
  setPendingContactAction(null);
};

// زر الإبلاغ عن الإعلان - مش بيعمل حاجة حاليًا
const handleReportAd = () => {
  // TODO: تفعيل منطق الإبلاغ لاحقًا
};

// فتح فورم الإبلاغ عن الإعلان مباشرة (زر مستقل جنب الاتصال والواتساب)
const REPORT_FORM_BASE_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSchiMdQiaVqRVlKG5uPVH_-9565dgbqHMHOPhseiIvr--ypGQ/viewform';
const REPORT_ENTRY_ID = 'entry.723940996'; // خانة "The Link for the Ad you want to report"

const handleReportButtonClick = () => {
  const adLink = typeof window !== 'undefined' ? window.location.href : '';
  const prefillUrl = `${REPORT_FORM_BASE_URL}?usp=pp_url&${REPORT_ENTRY_ID}=${encodeURIComponent(adLink)}`;
  window.open(prefillUrl, '_blank', 'noopener,noreferrer');
};

  if (loading) {
    return (
      <div className="ad-page">
        <div className="ad-container">
          <div className="ad-header">
            <Link to="/" className="close-button">×</Link>
            <h2>{getLocalizedText('تفاصيل الإعلان', 'Ad Details')}</h2>
          </div>
          <div className="ad-body">
            <p style={{ textAlign: 'center', padding: '40px' }}>
              {getLocalizedText('جاري تحميل الإعلان...', 'Loading ad...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="ad-page">
        <div className="ad-container">
          <div className="ad-header">
            <Link to="/" className="close-button">×</Link>
            <h2>{getLocalizedText('تفاصيل الإعلان', 'Ad Details')}</h2>
          </div>
          <div className="ad-body">
            <p>{getLocalizedText('الإعلان غير موجود.', 'Ad not found.')}</p>
          </div>
        </div>
      </div>
    );
  }

  const locationText = currentLanguage === 'ar' ? listing.location.ar : listing.location.en;
  const priceTextMain = currentLanguage === 'ar'
    ? `${listing.price.toLocaleString('ar-EG')} جنيهًا مصريًا`
    : `${listing.price.toLocaleString()} EGP`;

  const priceDurationText = listing.priceDuration === 'monthly' ? 
    (currentLanguage === 'ar' ? 'شهريًا' : 'Monthly') :
    listing.priceDuration === 'weekly' ? 
    (currentLanguage === 'ar' ? 'أسبوعيًا' : 'Weekly') :
    (currentLanguage === 'ar' ? 'يوميًا' : 'Daily');

  const priceTextSub = currentLanguage === 'ar'
    ? `${priceDurationText} (${listing.insurance.toLocaleString('ar-EG')} ${getLocalizedText('تأمين', 'Insurance')})`
    : `${priceDurationText} (${listing.insurance.toLocaleString()} ${getLocalizedText('تأمين', 'Insurance')})`;
  
  const accommodationType = currentLanguage === 'ar' ? listing.type.ar : listing.type.en;
  const amenities = currentLanguage === 'ar' ? listing.amenities.ar : listing.amenities.en;
  const timeAgo = currentLanguage === 'ar' ? listing.timeSincePosted.ar : listing.timeSincePosted.en;

  const getPolicyText = (policy) => {
    if (policy === 'allowed') return currentLanguage === 'ar' ? 'مسموح' : 'Allowed';
    if (policy === 'not_allowed') return currentLanguage === 'ar' ? 'غير مسموح' : 'Not Allowed';
    return currentLanguage === 'ar' ? 'غير محدد' : 'Not specified';
  };

  // جديد: ترجمة نوع المُعلن (لو موجود، الإعلانات القديمة ممكن ميكونش عندها القيمة دي)
  const getHostTypeText = (hostType) => {
    if (hostType === 'owner') return currentLanguage === 'ar' ? 'مالك للوحدة' : 'Property Owner';
    if (hostType === 'tenant') return currentLanguage === 'ar' ? 'المستأجر الحالي' : 'Current Tenant';
    if (hostType === 'manager') return currentLanguage === 'ar' ? 'مدير للوحدة' : 'Managing the unit';
    return '';
  };

  // جديد: نص نوع المُعلن - هيبقى فاضي لو الإعلان قديم ومفيهوش القيمة دي، فمش هيتعرض
  const hostTypeText = getHostTypeText(listing.hostType);

  const billsIncluded = listing.priceIncludesBills
    ? getLocalizedText('يشمل الفواتير', 'Includes bills')
    : getLocalizedText('لا يشمل الفواتير', 'Excludes bills');

  return (
    <div className="ad-page">
      <div className="ad-container">
        <div className="ad-header">
          <Link to="/" className="close-button">×</Link>
          <h2>{getLocalizedText('أبحث عن شريك سكن', 'Looking For a Roommate')}</h2>
        </div>
        <div className="ad-body">
          <div className="ad-image-section">
           <div className="ad-image-container">
             <img 
               src={getOptimizedImageUrl(listing.images[currentImageIndex], 'detail') || '/assets/room-1.jpg'} 
               alt={getLocalizedText('صورة الإعلان', 'Listing Image')} 
               className="ad-image" 
               style={{
                 transform: `scale(${zoomLevel})`,
                 transition: 'transform 0.3s ease',
                 cursor: zoomLevel > 1 ? 'zoom-out' : 'zoom-in'
               }}
               onClick={handleResetZoom}
            />
           {listing.images.length > 1 && (
             <>
               <button className="image-nav-button prev-button" onClick={handlePrevImage}>
                 <FontAwesomeIcon icon={faAngleLeft} />
               </button>
               <button className="image-nav-button next-button" onClick={handleNextImage}>
                 <FontAwesomeIcon icon={faAngleRight} />
               </button>
             </>
           )}
           
           <div className="zoom-controls">
             {/* زر التصغير المحدث */}
             <button 
               className="zoom-button zoom-out" 
               onClick={handleZoomOut} 
               disabled={zoomLevel <= MIN_ZOOM}
             >
               <span>−</span>
             </button>
             <button className="zoom-button zoom-in" onClick={handleZoomIn} disabled={zoomLevel >= 3}>
               <span>+</span>
             </button>
            <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
           </div>
         </div>
            {listing.images.length > 1 && (
              <div className="image-thumbnails">
                {listing.images.map((img, index) => (
                  <img
                    key={index}
                    src={getOptimizedImageUrl(img, 'thumbnail')}
                    alt={`Thumbnail ${index + 1}`}
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="ad-info">
            <div className="ad-info-icons">
              <button 
                className={`favorite-btn ${isFavorited ? 'favorited' : ''}`} 
                onClick={toggleFavorite}
                disabled={loadingFavorite}
              >
                <FontAwesomeIcon icon={isFavorited ? faHeartSolid : faHeartRegular} />
              </button>
            </div>
                        {hostTypeText && (
              <div className="host-type-badge">
                {hostTypeText}
              </div>
            )}
            <div className="ad-accommodation-type">
               {accommodationType}
            </div>

            <div className="ad-price">
              <span className="price-main">{priceTextMain}</span>
              <span className="price-sub">{priceTextSub}</span>
              <span className="bills-info">{billsIncluded}</span>
            </div>

            <div className="ad-location">
                <h3>{getLocalizedText('الموقع:', 'Location:')}</h3>
              <FontAwesomeIcon icon={faLocationDot} />
              <span className="location-text">{locationText}</span>
              <span className="time-ago"> • {timeAgo}</span>
            </div>
            <div className="full-address-section">
              <span className="address-label">
                {currentLanguage === 'ar' ? 'العنوان:' : 'Address:'}
              </span>
              <span className="address-value"> {listing.fullAddress}</span>
            </div>
            {listing.description && (
              <div className="description-section">
                <span className="description-label">
                  {currentLanguage === 'ar' ? 'الوصف:' : 'Description:'}
                </span>
                <div className="description-value">{listing.description}</div>
              </div>
            )}
            {/* جديد: القيمة المضافة من مدير العقار - بتظهر بس لو الإعلان جديد وفيه القيمة دي */}
            {listing.hostType === 'manager' && listing.hostManagerValue && (
              <div className="description-section">
                <span className="description-label">
                  {getLocalizedText('الخدمة المقدمة من مدير الوحدة:', 'Service offered by the unit manager:')}
                </span>
                <div className="description-value">{listing.hostManagerValue}</div>
              </div>
            )}
           
            <div className="ad-amenities">
              <h3>{getLocalizedText('المرافق', 'Amenities')}</h3>
              <div className="amenities-list">
                {amenities.map((amenity, index) => (
                  <span key={index} className="amenity-tag">{amenity}</span>
                ))}
              </div>
            </div>
            <div className="ad-policies">
              <div className="policy-item">
                <span className="policy-label">{getLocalizedText('سياسة التدخين', 'Smoking Policy')}:</span>
                <span className="policy-value">{getPolicyText(listing.smokingPolicy)}</span>
              </div>
              <div className="policy-item">
                <span className="policy-label">{getLocalizedText('سياسة الزوار', 'Visitors Policy')}:</span>
                <span className="policy-value">{getPolicyText(listing.visitorsPolicy)}</span>
              </div>
              <div className="policy-item">
                <span className="policy-label">{getLocalizedText('سياسة الحيوانات الأليفة', 'Pets Policy')}:</span>
                <span className="policy-value">{getPolicyText(listing.petsPolicy)}</span>
              </div>
            </div>
                        {listing.cleaningExplanation && (
              <div className="description-section">
                <span className="description-label">
                  {getLocalizedText('عملية التنظيف أثناء فترة الإقامة:', 'Cleaning process during residence:')}
                </span>
                <div className="description-value">{listing.cleaningExplanation}</div>
              </div>
            )}
          </div>
        </div>
        <div className="ad-footer">
          <div className="ad-actions">
            <button className="action-btn call-btn" onClick={handleCallClick}>
              <FontAwesomeIcon icon={faPhone} />
              <span>{getLocalizedText('اتصال', 'Call')}</span>
            </button>
            <button className="action-btn whatsapp-btn" onClick={handleWhatsAppClick}>
              <FontAwesomeIcon icon={faWhatsapp} />
              <span>{getLocalizedText('واتساب', 'WhatsApp')}</span>
            </button>
            <button
              type="button"
              className="action-btn report-btn"
              onClick={handleReportButtonClick}
              title={getLocalizedText('إبلاغ عن الإعلان', 'Report this ad')}
              aria-label={getLocalizedText('إبلاغ عن الإعلان', 'Report this ad')}
            >
              <FontAwesomeIcon icon={faFlag} />
              <span>{getLocalizedText('إبلاغ', 'Report')}</span>
            </button>
            <div className="posted-by">
              {getLocalizedText('منشور من', 'Posted by')} {listing.postedBy}
            </div>
          </div>
        </div>
      </div>

      {/* مودال التحذير قبل التواصل مع صاحب الإعلان */}
      <ContactSafetyModal
        isOpen={showContactSafetyModal}
        actionType={pendingContactAction}
        currentLanguage={currentLanguage}
        getLocalizedText={getLocalizedText}
        onClose={handleCloseContactSafetyModal}
        onConfirm={handleConfirmContact}
        onReport={handleReportAd}
        adLink={typeof window !== 'undefined' ? window.location.href : ''}
      />
    </div>
  );
}

export default Ad;